/** Single-player setup (the operation order) and the scenario briefings. */
export const setup = {
  'sp.kicker': 'Operation order · Single player',
  'sp.title': 'Plan the Campaign',
  'sp.footer': 'Single player runs entirely in your browser. Pause at any time with Space.',
  'sp.theatre': 'Theatre',
  'sp.map': 'Map',
  'sp.map.generated': 'Fictional map (generated from the seed)',
  'sp.map.hint': 'A historical map of Europe — or, last in the list, a fictional continent.',
  'sp.map.meta': '{year} · {w} × {h} hexes · {km} km a hex',
  'sp.map.neutral': 'Neutral countries (hatched) are closed to every army.',
  'sp.map.preview': 'Map of the theatre',
  'sp.nation.you': 'Human · your command',
  'sp.nation.take': 'Command',
  'sp.nation.takeHint': 'Command this nation yourself',
  'theatre.poland-1939.desc':
    'Germany against Poland: the Corridor, the Vistula and the Bzura — and East Prussia cut off behind the Polish lines.',
  'theatre.east-prussia-1914.desc':
    'The German Empire against the Russian Empire: the Masurian lakes, the forests of Johannisburg and the road to Tannenberg.',
  'theatre.europe-1939.desc':
    'The continent on 1 September 1939: Germany, France, Poland, the Soviet Union, Italy and Britain (reachable across the Channel at Dover) — every other country neutral.',
  'theatre.moscow-1941.desc':
    'The Soviet Union against Germany before Moscow: Vyazma, Bryansk and the Mozhaysk line as the autumn rains begin.',
  'theatre.stalingrad-1942.desc':
    'The Soviet Union against Germany, Romania and Italy: the city on the Volga, the Don bend and the thin Romanian flanks.',
  'theatre.normandy-1944.desc':
    'The United States and the United Kingdom against Germany: the bocage, Caen and Saint-Lô — and the Pas-de-Calais waiting.',
  'sp.seed': 'Map seed',
  'sp.seed.hint': 'The same seed always draws the same theatre.',
  'sp.seed.aria': 'Seed',
  'sp.seed.roll': 'Roll a new seed',
  'sp.speed': 'Game speed',
  'sp.season': 'Season',
  'sp.belligerents': 'Belligerents',
  'sp.ffa': 'Free for all',
  'sp.customTeams': 'Custom teams',
  'sp.alliesNote': 'Allies share vision and win together.',
  'sp.you': 'You — Commander-in-Chief',
  'sp.youSub': 'Human · faction I',
  'sp.opponent': 'Opponent {num}',
  'sp.opponentSub': 'Computer · {difficulty}',
  'sp.removeOpponent': 'Remove opponent',
  'sp.addOpponent': 'Add opponent',
  'sp.factionCount': '{n} of 6 factions',
  'sp.oneTeam': 'Everyone is on one team — split them up.',
  'sp.rules': 'Rules of engagement',
  'sp.dayLimit': 'Day limit',
  'sp.dayLimit.hint': 'When time runs out, the most victory points wins.',
  'sp.decisive': 'Decisive victory · {pct} VP',
  'sp.decisive.hint': 'Hold this share of all victory points for 24 hours to win outright.',
  'sp.decisive.aria': 'Victory share',
  'sp.techs': 'Starting technology',
  'sp.brief.head': 'GENERAL STAFF',
  'sp.brief.no': 'No. {n}',
  'sp.brief.stamp': 'Secret',
  'sp.brief.title': 'OPERATION ORDER',
  'sp.brief.theatre': 'Theatre',
  'sp.brief.season': 'Season',
  'sp.brief.belligerents': 'Belligerents',
  'sp.brief.belligerentsVal': '{n} (you + {ai} AI)',
  'sp.brief.alignment': 'Alignment',
  'sp.brief.versus': '{a} v {b}',
  'sp.brief.victory': 'Victory',
  'sp.brief.victoryVal': '{pct} VP · 24 h',
  'sp.brief.timeLimit': 'Time limit',
  'sp.brief.technology': 'Technology',
  'sp.brief.tempo': 'Tempo',
  'sp.brief.opposition': 'Opposition',
  'sp.brief.note':
    'H-hour is Day 1, 0600. Orders leave headquarters by telephone and courier. Protect the couriers; the enemy reads captured mail. Teams: {teams}.',
  'sp.brief.noTeams': 'none — every nation for itself',
  'sp.brief.approved': 'Approved,',
  'sp.brief.signature': 'v. Dornheim',
  'sp.launching': 'Mobilising…',
  'sp.launch': 'Commence operations',
  'sp.timeout': 'The war ministry did not answer — the single-player host failed to start.',

  // ---- scenarios
  'scn.kicker': 'Historical situations · Single player',
  'scn.title': 'Scenarios',
  'scn.footer': 'Fought on historical maps of Europe where one exists; the AI plays by the same rules.',
  'scn.inspired': 'After {what}',
  'scn.situation': 'Situation',
  'scn.forces': 'Forces',
  'scn.theatre': '{size} theatre · {season}',
  'scn.theatreReal': '{name} · {season}',
  'scn.nations': { one: '{n} nation', other: '{n} nations' },
  'scn.dayLimit': '{n}-day limit',
  'scn.victory': 'victory at {pct} VP',
  'scn.everyone': 'All sides',
  'scn.you': 'You',
  'scn.enemy': 'Enemy {num}',
  'scn.army': 'army ×{x}',
  'scn.reserve': 'reserve {n} × {unit}',
  'scn.nothingSpecial': 'standard forces',
  'scn.opposition': 'Opposition',
  'scn.asBriefed': 'As briefed',
  'scn.asBriefed.hint': 'Each enemy as the scenario sets him',
  'scn.seed': 'Theatre',
  'scn.take': 'Take command',
  'scn.stamp': 'Most secret',
  'scn.select': 'Select',

  'scn.tannenberg.title': 'The Tannenberg Protocol',
  'scn.tannenberg.inspired': 'Tannenberg, August 1914',
  'scn.tannenberg.brief':
    'The enemy has crossed the frontier in superior strength. His staffs give their orders by radio — and they do not encipher them. Listen, meet each thrust where it is aimed, and close the ring.',
  'scn.tannenberg.lesson': 'Outnumbered — but he talks in the clear.',
  'scn.rasputitsa.title': 'Rasputitsa',
  'scn.rasputitsa.inspired': 'Operation Typhoon, October 1941',
  'scn.rasputitsa.brief':
    'October 1941. Army Group Centre drives on Moscow with fresh armour, stronger than we are. The autumn rains are coming, and every day we hold makes the roads worse for it. Trade ground for time, keep the army whole, and strike when its tanks sit in the mud.',
  'scn.rasputitsa.lesson': 'Hold until the ground turns against him.',
  'scn.ghost-army.title': 'The Ghost Army',
  'scn.ghost-army.inspired': 'Operation Fortitude and the Ghost Army, 1944',
  'scn.ghost-army.brief':
    'Normandy, July 1944. The lodgement holds, but the hedgerows are a wall. The enemy still keeps an army at Calais, waiting for the invasion he believes is coming. Five divisions of canvas and plywood stand ready; from the air they pass for the real thing. Make him defend where we are not — then break out where he is weak.',
  'scn.ghost-army.lesson': 'Make them defend where you are not.',
  'scn.enigma.title': 'Enigma',
  'scn.enigma.inspired': 'The Polish Cipher Bureau, September 1939',
  'scn.enigma.brief':
    'September 1939. Germany attacks from three sides — out of Pomerania, Silesia and Slovakia, and from East Prussia behind the Corridor. His armies command by radio through the Enigma machine, which he believes unbreakable. Our Cipher Bureau broke it years ago. Read his orders, meet each thrust before it lands — but sparingly, lest he learn that his mail lies open.',
  'scn.enigma.lesson': 'Use what you read without giving it away.',
  'scn.winter-kessel.title': 'Winter Kessel',
  'scn.winter-kessel.inspired': 'Operation Uranus, November 1942',
  'scn.winter-kessel.brief':
    'November 1942. The enemy’s 6th Army is fighting for the last streets of Stalingrad; its flanks on the Don and among the lakes south of the city are held by Romanian armies, thin and poorly equipped. Strike both flanks, meet at Kalach, and close the ring before the winter deepens — snow starves whoever is cut off.',
  'scn.winter-kessel.lesson': 'Break the weak flanks; snow starves the encircled.',
} as const;
